// frontend/src/pages/BulkMemberAccountsPage.jsx
// Admin tool: upload a CSV to bulk-create member login accounts

import React, { useState, useRef } from 'react';
import AdminLayout from './AdminLayout';
import api from '../utils/apiClient';

const REQUIRED_COLS = ['email', 'password', 'displayname'];
const OPTIONAL_COLS = ['memberid'];
const ALL_COLS = ['email', 'password', 'displayname', 'memberid'];
const COL_LABELS = { email: 'email', password: 'password', displayname: 'displayName', memberid: 'memberId' };

// ── CSV parser ──────────────────────────────────────────────────────────────
function parseCSV(text) {
  const lines = text.trim().split(/\r?\n/).filter(l => l.trim());
  if (lines.length < 2) return { headers: [], rows: [], error: 'CSV must have a header row and at least one data row.' };

  const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/^"|"$/g, ''));
  const rows = lines.slice(1).map((line, idx) => {
    const values = line.split(',').map(v => v.trim().replace(/^"|"$/g, ''));
    const row = {};
    headers.forEach((h, i) => { row[h] = values[i] || ''; });
    row._line = idx + 2;
    return row;
  });

  return { headers, rows, error: null };
}

// ── Component ────────────────────────────────────────────────────────────────
const BulkMemberAccountsPage = () => {
  const [step, setStep] = useState('upload'); // upload | preview | result
  const [csvRows, setCsvRows] = useState([]);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [parseError, setParseError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef();

  // ── Handle file select ───────────────────────────────────────────────────
  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setParseError('');
    const reader = new FileReader();
    reader.onload = (ev) => {
      const { headers, rows, error } = parseCSV(ev.target.result);
      if (error) { setParseError(error); return; }

      // Validate required columns
      const missing = REQUIRED_COLS.filter(c => !headers.includes(c));
      if (missing.length) {
        setParseError(`Missing required column(s): ${missing.join(', ')}`);
        return;
      }

      setCsvHeaders(headers);
      setCsvRows(rows);
      setStep('preview');
    };
    reader.readAsText(file);
  };

  // ── Submit ───────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!window.confirm(`Create accounts for ${csvRows.length} members? This cannot be undone.`)) return;

    setSubmitting(true);
    try {
      const payload = csvRows.map(row => ({
        email: row.email,
        password: row.password,
        displayName: row.displayname || '',
        memberId: row.memberid || '',
      }));

      const res = await api.post('/users/bulk-create-members', { members: payload });
      const data = await res.json();

      if (data.success) {
        setResult(data);
        setStep('result');
      } else {
        alert('Error: ' + (data.error || 'Unknown error'));
      }
    } catch (err) {
      alert('Request failed: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Reset ────────────────────────────────────────────────────────────────
  const handleReset = () => {
    setCsvRows([]);
    setCsvHeaders([]);
    setParseError('');
    setResult(null);
    setStep('upload');
    if (fileRef.current) fileRef.current.value = '';
  };

  // ── Download sample CSV ──────────────────────────────────────────────────
  const downloadSample = () => {
    const sample = [
      'email,password,displayName,memberId',
      'john.doe@email.com,Pass1234,John Doe,S00101',
      'jane.smith@email.com,Pass1234,Jane Smith,S00102',
    ].join('\n');
    const blob = new Blob([sample], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'member_accounts_sample.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // ────────────────────────────────────────────────────────────────────────
  return (
    <AdminLayout>
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px 16px' }}>
        <h2 style={{ marginBottom: 4 }}>Bulk Create Member Accounts</h2>
        <p style={{ color: '#666', marginBottom: 24 }}>
          Upload a CSV to create login accounts for multiple members at once. Each member will be
          able to sign in to the member portal immediately after creation.
        </p>

        {/* ── STEP 1: UPLOAD ── */}
        {step === 'upload' && (
          <div style={styles.card}>
            <h3 style={{ marginTop: 0 }}>Step 1 — Prepare your CSV</h3>

            <div style={styles.infoBox}>
              <strong>Required columns:</strong>
              <ul style={{ margin: '8px 0 8px 20px', padding: 0 }}>
                <li><code>email</code> — member's email address</li>
                <li><code>password</code> — initial password (min 6 chars)</li>
                <li><code>displayName</code> — full name</li>
              </ul>
              <strong>Optional:</strong>
              <ul style={{ margin: '8px 0 0 20px', padding: 0 }}>
                <li><code>memberId</code> — SACCO member ID (e.g. S00101) to link to existing member record</li>
              </ul>
            </div>

            <div style={{ marginBottom: 16 }}>
              <button onClick={downloadSample} style={styles.btnSecondary}>
                ⬇ Download Sample CSV
              </button>
            </div>

            <label style={styles.fileLabel}>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                onChange={handleFile}
                style={{ display: 'none' }}
              />
              📂 Choose CSV File
            </label>

            {parseError && (
              <div style={styles.errorBox}>{parseError}</div>
            )}
          </div>
        )}

        {/* ── STEP 2: PREVIEW ── */}
        {step === 'preview' && (
          <div style={styles.card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0 }}>Step 2 — Preview ({csvRows.length} members)</h3>
              <button onClick={handleReset} style={styles.btnSecondary}>← Back</button>
            </div>

            <div style={{ overflowX: 'auto', marginBottom: 20 }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>#</th>
                    {ALL_COLS.map(c => (
                      <th key={c} style={styles.th}>{COL_LABELS[c] || c}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {csvRows.slice(0, 50).map((row, i) => (
                    <tr key={i} style={i % 2 === 0 ? {} : { background: '#f9f9f9' }}>
                      <td style={styles.td}>{row._line}</td>
                      {ALL_COLS.map(c => (
                        <td key={c} style={styles.td}>
                          {c === 'password'
                            ? '••••••'
                            : (row[c.toLowerCase()] || <span style={{ color: '#aaa' }}>—</span>)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {csvRows.length > 50 && (
                <p style={{ color: '#888', fontSize: 13 }}>
                  Showing first 50 of {csvRows.length} rows. All rows will be processed on submit.
                </p>
              )}
            </div>

            <div style={styles.warningBox}>
              ⚠️ Accounts are created immediately and marked <strong>active</strong>. Members can log in right away.
              Make sure passwords are communicated to each member securely (e.g. via SMS or email).
            </div>

            <button
              onClick={handleSubmit}
              disabled={submitting}
              style={styles.btnPrimary}
            >
              {submitting
                ? `⏳ Creating accounts... (${csvRows.length} members)`
                : `✅ Create ${csvRows.length} Member Accounts`}
            </button>
          </div>
        )}

        {/* ── STEP 3: RESULT ── */}
        {step === 'result' && result && (
          <div style={styles.card}>
            <h3 style={{ marginTop: 0 }}>Results</h3>

            {/* Summary */}
            <div style={styles.summaryGrid}>
              <div style={{ ...styles.summaryBox, background: '#e6f4ea', borderColor: '#4caf50' }}>
                <div style={styles.summaryNum}>{result.summary.created}</div>
                <div>Created ✅</div>
              </div>
              <div style={{ ...styles.summaryBox, background: '#fff8e1', borderColor: '#ff9800' }}>
                <div style={styles.summaryNum}>{result.summary.skipped}</div>
                <div>Skipped (already exist)</div>
              </div>
              <div style={{ ...styles.summaryBox, background: '#fce4ec', borderColor: '#e53935' }}>
                <div style={styles.summaryNum}>{result.summary.failed}</div>
                <div>Failed ❌</div>
              </div>
            </div>

            {/* Skipped detail */}
            {result.details.skipped.length > 0 && (
              <details style={{ marginBottom: 12 }}>
                <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
                  Skipped — emails already registered ({result.details.skipped.length})
                </summary>
                <ul style={{ marginTop: 8 }}>
                  {result.details.skipped.map((s, i) => (
                    <li key={i}>{s.email} — {s.reason}</li>
                  ))}
                </ul>
              </details>
            )}

            {/* Failed detail */}
            {result.details.failed.length > 0 && (
              <details style={{ marginBottom: 12 }}>
                <summary style={{ cursor: 'pointer', fontWeight: 600, color: '#c62828' }}>
                  Failed ({result.details.failed.length})
                </summary>
                <ul style={{ marginTop: 8 }}>
                  {result.details.failed.map((f, i) => (
                    <li key={i}>{f.email} — {f.reason}</li>
                  ))}
                </ul>
              </details>
            )}

            <button onClick={handleReset} style={styles.btnPrimary}>
              ↩ Start Another Import
            </button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

// ── Inline styles ─────────────────────────────────────────────────────────
const styles = {
  card: {
    background: '#fff',
    border: '1px solid #ddd',
    borderRadius: 8,
    padding: 24,
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
  },
  infoBox: {
    background: '#e8f0fe',
    border: '1px solid #a8c4f7',
    borderRadius: 6,
    padding: '12px 16px',
    marginBottom: 16,
    fontSize: 14,
  },
  warningBox: {
    background: '#fff8e1',
    border: '1px solid #ffcc02',
    borderRadius: 6,
    padding: '10px 14px',
    marginBottom: 16,
    fontSize: 13,
  },
  errorBox: {
    background: '#fce4ec',
    border: '1px solid #e53935',
    borderRadius: 6,
    padding: '10px 14px',
    marginTop: 12,
    color: '#c62828',
    fontSize: 14,
  },
  fileLabel: {
    display: 'inline-block',
    padding: '10px 20px',
    background: '#1976d2',
    color: '#fff',
    borderRadius: 6,
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: 14,
  },
  btnPrimary: {
    padding: '10px 24px',
    background: '#1976d2',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    fontWeight: 600,
    fontSize: 14,
    cursor: 'pointer',
  },
  btnSecondary: {
    padding: '8px 16px',
    background: '#fff',
    color: '#333',
    border: '1px solid #ccc',
    borderRadius: 6,
    fontWeight: 500,
    fontSize: 13,
    cursor: 'pointer',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 13,
  },
  th: {
    background: '#f5f5f5',
    padding: '8px 10px',
    textAlign: 'left',
    borderBottom: '2px solid #ddd',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  td: {
    padding: '7px 10px',
    borderBottom: '1px solid #eee',
    whiteSpace: 'nowrap',
  },
  summaryGrid: {
    display: 'flex',
    gap: 12,
    marginBottom: 20,
    flexWrap: 'wrap',
  },
  summaryBox: {
    flex: 1,
    minWidth: 120,
    border: '2px solid',
    borderRadius: 8,
    padding: '14px 16px',
    textAlign: 'center',
    fontSize: 14,
  },
  summaryNum: {
    fontSize: 32,
    fontWeight: 700,
    lineHeight: 1.1,
    marginBottom: 4,
  },
};

export default BulkMemberAccountsPage;
